import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '../lib/errorReporter';
import { esErrorDeChunk, recargarPorVersionNueva } from '../lib/chunkReload';

/** El portal del cliente (/p/...) nunca muestra la pantalla técnica de error. */
const esPortalCliente = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/p/');

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
  recargando: boolean;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorId: null,
    recargando: false,
  };

  private reintentosSuaves = 0;

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId: Math.random().toString(36).slice(2, 8).toUpperCase(),
      recargando: false,
    };
  }

  async componentDidCatch(error: Error, info: ErrorInfo) {
    // Archivo de una versión anterior que ya no existe: se recarga solo, sin avisar ni reportar.
    if (esErrorDeChunk(error) && recargarPorVersionNueva()) {
      this.setState({ recargando: true });
      return;
    }
    // En el portal del cliente se reintenta una vez en silencio antes de mostrar nada.
    if (esPortalCliente() && this.reintentosSuaves < 1) {
      this.reintentosSuaves += 1;
      window.setTimeout(() => this.setState({ hasError: false, error: null, errorId: null }), 0);
    }
    await reportError({
      error_message: error.message,
      error_type: 'render',
      error_code: `RENDER-${error.name}`,
      component: info.componentStack?.split('\n')[1]?.trim() ?? 'unknown',
      page_route: window.location.pathname,
      action: 'render',
      user_message: 'La pantalla no pudo cargar',
      context_data: {
        error_name: error.name,
        stack: error.stack?.slice(0, 500),
        component_stack: info.componentStack?.slice(0, 500),
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorId: null, recargando: false });
  };

  handleRecargar = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.state.recargando) {
        return (
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #F2F5EE)', color: 'var(--mu, #6B7E99)', fontFamily: 'var(--sans, system-ui, sans-serif)', fontSize: '14px' }}>
            Actualizando...
          </div>
        );
      }
      if (this.props.fallback) return this.props.fallback;

      if (esPortalCliente()) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '24px', textAlign: 'center', fontFamily: 'var(--sans, system-ui, sans-serif)', background: 'var(--bg, #F2F5EE)' }}>
            <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--fg, #141E2E)', marginBottom: '6px' }}>Un momento</p>
            <p style={{ fontSize: '14px', color: 'var(--fg2, #2D3F58)', marginBottom: '20px', maxWidth: '280px', lineHeight: 1.5 }}>
              Estamos terminando de cargar tu propuesta. Toca para continuar.
            </p>
            <button
              onClick={this.handleRecargar}
              style={{ padding: '12px 24px', background: 'var(--p, #748D44)', color: 'var(--w, #fff)', border: 'none', borderRadius: 'var(--r, 12px)', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans, system-ui, sans-serif)' }}
            >
              Continuar
            </button>
          </div>
        );
      }

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '24px',
          textAlign: 'center',
          fontFamily: 'var(--sans, system-ui, sans-serif)',
          background: 'var(--bg, #F2F5EE)',
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--mu, #6B7E99)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
            <circle cx="12" cy="12" r="10"/>
            <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
          <h1 style={{
            fontSize: '20px',
            fontWeight: 600,
            color: 'var(--fg, #141E2E)',
            marginBottom: '8px',
          }}>
            Algo salio mal
          </h1>
          <p style={{
            fontSize: '14px',
            color: 'var(--fg2, #2D3F58)',
            marginBottom: '4px',
            maxWidth: '300px',
            lineHeight: 1.5,
          }}>
            Esta pantalla tuvo un problema.
            Ya notificamos al equipo tecnico.
          </p>
          <p style={{
            fontSize: '11px',
            color: 'var(--mu, #6B7E99)',
            marginBottom: '24px',
            fontFamily: 'var(--mono, monospace)',
          }}>
            Ref: {this.state.errorId}
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '10px 20px',
                background: 'var(--p, #748D44)',
                color: 'var(--w, #fff)',
                border: 'none',
                borderRadius: 'var(--r, 12px)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--sans, system-ui, sans-serif)',
              }}
            >
              Reintentar
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                color: 'var(--fg2, #2D3F58)',
                border: '1px solid var(--bd, rgba(20,30,46,.08))',
                borderRadius: 'var(--r, 12px)',
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: 'var(--sans, system-ui, sans-serif)',
              }}
            >
              Ir al inicio
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
